import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

/* Mapping van internal tournament type-slugs naar wiki-artikel slugs. */
const WIKI_SLUG_MAP = {
  single_elimination: 'single-elimination',
  double_elimination: 'double-elimination',
  round_robin: 'round-robin',
  deathmatch: 'deathmatch',
  swiss: 'swiss',
  group_ko: 'group-stage-knockout',
  ffa: 'ffa-rounds',
  ladder: 'ladder-koth',
  consolation: 'consolation-bracket',
}
function wikiSlugForType(t) { return WIKI_SLUG_MAP[t] || 'begrippen' }

import { io } from 'socket.io-client'
import {
  Trophy,
  Users,
  Calendar,
  ArrowLeft,
  Play,
  Loader2,
  RefreshCw,
  Award,
  Target,
  Ban,
  Trash2,
  UserMinus,
  Flag,
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Edit,
  RotateCcw,
  Clock,
  GripVertical,
  MoreVertical
} from 'lucide-react'
import { useConfirm } from '../hooks/useConfirm'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { tournamentsAPI, authAPI, statsAPI, pointsAPI, prizesAPI, screenshotsAPI, badgeAPI } from '@/lib/api'
import { TOURNAMENT_FORMATS } from '@/lib/tournamentFormats'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import TournamentBracket from '@/components/TournamentBracket'
import MatchResultDialog from '@/components/MatchResultDialog'
import FFAScoreDialog from '@/components/FFAScoreDialog'
import {
  openMIDI as tOpenMIDI, listInputs as tListInputs, attachInputListener as tAttachInput,
  eventKey as tEventKey, loadMappings as tLoadMappings, loadInputId as tLoadInputId,
  normalizeMapping as tNormalizeMapping,
} from '@/lib/tournamentsMidi'

export default function TournamentDetailsPage() {
  const confirm = useConfirm()
  const { id } = useParams()
  const navigate = useNavigate()

  // State
  const [tournament, setTournament] = useState(null)
  const [participants, setParticipants] = useState([])
  const [walkInOpen, setWalkInOpen] = useState(false)
  const [walkInQ, setWalkInQ] = useState('')
  const [walkInList, setWalkInList] = useState([])
  const [walkInBusy, setWalkInBusy] = useState(false)

  useEffect(() => {
    if (!walkInOpen) return
    const t = setTimeout(async () => {
      try {
        const r = await authAPI.searchUsers(walkInQ || '')
        setWalkInList(r.data?.users || [])
      } catch (e) { setWalkInList([]) }
    }, 200)
    return () => clearTimeout(t)
  }, [walkInOpen, walkInQ])
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)

  // Actions state
  const [starting, setStarting] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [ffaHeatOpen, setFfaHeatOpen] = useState(false)
  const [matchDialogOpen, setMatchDialogOpen] = useState(false)
  const [updatingMatch, setUpdatingMatch] = useState(false)
  const [removingParticipant, setRemovingParticipant] = useState(null)
  const [markingGivenUp, setMarkingGivenUp] = useState(null)

  // Deathmatch score state
  const [editingScores, setEditingScores] = useState({}) // matchId -> {participantId -> score}
  const [savingScores, setSavingScores] = useState(null) // matchId being saved
  const [openRounds, setOpenRounds] = useState({}) // matchId -> boolean (for accordion)

  // Edit tournament state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    game: '',
    description: '',
    rules: '',
    max_participants: '',
    start_time: ''
  })
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState(null)

  // Restart tournament state
  const [restarting, setRestarting] = useState(false)

  // Points award state
  const [awardDialogOpen, setAwardDialogOpen] = useState(false)
  const [awardPoints, setAwardPoints] = useState({ 1: 100, 2: 75, 3: 50, participation: 25 })
  const [awarding, setAwarding] = useState(false)
  const [awardMsg, setAwardMsg] = useState(null)

  // Match scheduling state
  const [matchSchedule, setMatchSchedule] = useState([])
  const [editingSchedule, setEditingSchedule] = useState(null) // matchId being edited
  const [scheduleTime, setScheduleTime] = useState('')
  const [updatingSchedule, setUpdatingSchedule] = useState(false)

  // Socket.IO
  const [socket, setSocket] = useState(null)
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem('tournament-active-tab') || 'bracket' } catch { return 'bracket' }
  })
  useEffect(() => { try { localStorage.setItem('tournament-active-tab', activeTab) } catch {} }, [activeTab])

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          const response = await authAPI.getCurrentUser()
          setCurrentUser(response.data.user)
        }
      } catch (err) {
        console.error('Failed to fetch current user:', err)
      }
    }
    fetchCurrentUser()
  }, [])

  // Fetch tournament details
  const fetchTournament = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await tournamentsAPI.getTournament(id)
      setTournament(response.data.tournament)
      setParticipants(response.data.participants || [])
      setMatches(response.data.matches || [])
    } catch (err) {
      console.error('Failed to fetch tournament:', err)
      setError(err.response?.data?.error || 'Failed to load tournament')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTournament()
  }, [id])

  // Socket.IO connection
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token || !id) return

    const newSocket = io({
      auth: { token },
      transports: ['websocket', 'polling']
    })

    newSocket.on('connect', () => {
      console.log('Connected to Socket.IO')
      newSocket.emit('join-tournament', id)
    })

    newSocket.on('tournament-started', (data) => {
      setTournament(data.tournament)
      setMatches(data.matches || [])
    })

    newSocket.on('match-updated', (data) => {
      // Update the specific match in the list
      setMatches(prev =>
        prev.map(m => m.id === data.match.id ? data.match : m)
      )
    })

    // Nieuwe colon-events: features-toggle, no-show cascade, swiss/group-KO/
    // ladder/consolation generation en participant-updates emitten allemaal
    // `tournament:updated` en `tournament:match:updated`. Zonder deze listener
    // miste de Details-page al die live-updates.
    const refreshAll = () => fetchTournament()
    newSocket.on('tournament:updated', (data) => {
      if (!data || data.id === undefined || String(data.id) === String(id) || data.id === Number(id)) {
        refreshAll()
      }
    })
    newSocket.on('tournament:match:updated', (data) => {
      if (String(data?.tournamentId) === String(id)) refreshAll()
    })

    newSocket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error)
    })

    setSocket(newSocket)

    return () => {
      if (newSocket) {
        newSocket.emit('leave-tournament', id)
        newSocket.disconnect()
      }
    }
  }, [id])

  // Start tournament
  const handleStartTournament = async () => {
    try {
      setStarting(true)
      setError(null)
      const response = await tournamentsAPI.startTournament(id)
      setTournament(response.data.tournament)
      setMatches(response.data.matches || [])
    } catch (err) {
      console.error('Failed to start tournament:', err)
      setError(err.response?.data?.error || 'Failed to start tournament')
    } finally {
      setStarting(false)
    }
  }

  // Open match result dialog — FFA-heats krijgen een aparte per-participant dialog.
  const handleMatchClick = (match) => {
    setSelectedMatch(match)
    if (tournament?.type === 'ffa' || match?.bracket_type === 'ffa') {
      setFfaHeatOpen(true)
    } else {
      setMatchDialogOpen(true)
    }
  }

  const handleFFAScoreSubmit = async (scoreList, opts = {}) => {
    try {
      setUpdatingMatch(true)
      await tournamentsAPI.updateDeathmatchScores(id, selectedMatch.id, scoreList)
      if (!opts.reset) {
        // Als er een non-zero som is markeren we de heat als completed.
        const som = scoreList.reduce((s, r) => s + Number(r.score || 0), 0)
        if (som > 0) {
          await tournamentsAPI.updateMatch(id, selectedMatch.id, { status: 'completed' })
        }
      } else {
        await tournamentsAPI.updateMatch(id, selectedMatch.id, { status: 'pending' })
      }
      setFfaHeatOpen(false)
      await fetchTournament()
    } catch (err) {
      throw err
    } finally {
      setUpdatingMatch(false)
    }
  }

  // Update match result
  const handleUpdateMatch = async (matchData) => {
    try {
      setUpdatingMatch(true)
      await tournamentsAPI.updateMatch(id, selectedMatch.id, matchData)
      // Socket.IO will handle the update
      setMatchDialogOpen(false)
    } catch (err) {
      console.error('Failed to update match:', err)
      throw err
    } finally {
      setUpdatingMatch(false)
    }
  }

  // Cancel tournament
  const handleCancelTournament = async () => {
    if (!(await confirm('Zeker dat je dit toernooi wil annuleren? Deze actie kan niet ongedaan gemaakt worden.'))) {
      return
    }

    try {
      setCanceling(true)
      setError(null)
      const response = await tournamentsAPI.cancelTournament(id)
      setTournament(response.data.tournament)
    } catch (err) {
      console.error('Failed to cancel tournament:', err)
      setError(err.response?.data?.error || 'Failed to cancel tournament')
    } finally {
      setCanceling(false)
    }
  }

  // Delete tournament
  const handleDeleteTournament = async () => {
    if (!(await confirm('Zeker dat je dit toernooi definitief wil verwijderen? Alle deelnemers en matches gaan mee. Deze actie kan niet ongedaan gemaakt worden.'))) {
      return
    }

    try {
      setDeleting(true)
      setError(null)
      await tournamentsAPI.deleteTournament(id)
      navigate('/tournaments')
    } catch (err) {
      console.error('Failed to delete tournament:', err)
      setError(err.response?.data?.error || 'Failed to delete tournament')
      setDeleting(false)
    }
  }

  // Open edit dialog
  const handleOpenEditDialog = () => {
    if (!tournament) return

    // Format start_time for datetime-local input
    let formattedStartTime = ''
    if (tournament.start_time) {
      const date = new Date(tournament.start_time)
      formattedStartTime = date.toISOString().slice(0, 16)
    }

    setEditForm({
      name: tournament.name || '',
      game: tournament.game || '',
      description: tournament.description || '',
      rules: tournament.rules || '',
      max_participants: tournament.max_participants || '',
      start_time: formattedStartTime
    })
    setEditError(null)
    setEditDialogOpen(true)
  }

  // Handle edit form change
  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  // Submit edit tournament
  const handleEditSubmit = async (e) => {
    e.preventDefault()

    try {
      setEditLoading(true)
      setEditError(null)

      await tournamentsAPI.updateTournament(id, editForm)

      setEditDialogOpen(false)
      await fetchTournament()
    } catch (err) {
      console.error('Failed to update tournament:', err)
      setEditError(err.response?.data?.error || 'Failed to update tournament')
    } finally {
      setEditLoading(false)
    }
  }

  // Restart tournament
  const handleRestartTournament = async () => {
    if (!(await confirm('Zeker dat je dit toernooi opnieuw wil starten? Het toernooi valt terug naar registratie-status en alle matches worden verwijderd. Deelnemers blijven bewaard. Actie kan niet ongedaan gemaakt worden.'))) {
      return
    }

    try {
      setRestarting(true)
      setError(null)

      // Update tournament status to registration and clear matches
      await tournamentsAPI.updateTournament(id, { status: 'registration' })

      // Refresh tournament data
      await fetchTournament()
    } catch (err) {
      console.error('Failed to restart tournament:', err)
      setError(err.response?.data?.error || 'Failed to restart tournament')
    } finally {
      setRestarting(false)
    }
  }

  // Award tournament points
  const handleAwardPoints = async () => {
    setAwarding(true); setAwardMsg(null)
    try {
      const placements = []
      for (const p of participants) {
        const rank = p.placement
        const pts = rank === 1 ? parseInt(awardPoints[1] || 0)
                  : rank === 2 ? parseInt(awardPoints[2] || 0)
                  : rank === 3 ? parseInt(awardPoints[3] || 0)
                  : parseInt(awardPoints.participation || 0)
        if (!pts) continue
        const label = rank === 1 ? '🥇 1e plaats' : rank === 2 ? '🥈 2e plaats' : rank === 3 ? '🥉 3e plaats' : 'Deelname'
        if (tournament.registration_type === 'team' && p.team_members?.length) {
          for (const m of p.team_members) {
            placements.push({ user_id: m.user_id, points: pts, placement_label: label })
          }
        } else if (p.user_id) {
          placements.push({ user_id: p.user_id, points: pts, placement_label: label })
        }
      }
      if (placements.length === 0) {
        setAwardMsg({ type: 'error', text: 'Geen deelnemers met user_id gevonden.' }); setAwarding(false); return
      }
      const r = await pointsAPI.awardTournament(id, placements)
      setAwardMsg({ type: 'ok', text: `${r.data.awarded} punten-toekenning(en) verstuurd!` })
    } catch (e) {
      setAwardMsg({ type: 'error', text: e.response?.data?.error || 'Fout bij uitkeren punten' })
    } finally { setAwarding(false) }
  }

  // Remove participant
  const handleRemoveParticipant = async (participantId, participantName) => {
    if (!(await confirm(`${participantName} echt uit dit toernooi verwijderen?`))) {
      return
    }

    try {
      setRemovingParticipant(participantId)
      setError(null)
      await tournamentsAPI.removeParticipant(id, participantId)
      // Refresh tournament data
      await fetchTournament()
    } catch (err) {
      console.error('Failed to remove participant:', err)
      setError(err.response?.data?.error || 'Failed to remove participant')
    } finally {
      setRemovingParticipant(null)
    }
  }

  // Mark participant as given up
  const handleMarkGivenUp = async (participantId, participantName) => {
    if (!(await confirm(`${participantName} markeren als opgegeven?`))) {
      return
    }

    try {
      setMarkingGivenUp(participantId)
      setError(null)
      await tournamentsAPI.markParticipantGivenUp(id, participantId)
      // Refresh tournament data
      await fetchTournament()
    } catch (err) {
      console.error('Failed to mark participant as given up:', err)
      setError(err.response?.data?.error || 'Failed to mark participant as given up')
    } finally {
      setMarkingGivenUp(null)
    }
  }

  // Deathmatch score handlers
  const handleScoreChange = (matchId, participantId, score) => {
    setEditingScores(prev => ({
      ...prev,
      [matchId]: {
        ...(prev[matchId] || {}),
        [participantId]: score
      }
    }))
  }

  const handleSaveScores = async (matchId) => {
    try {
      setSavingScores(matchId)
      setError(null)

      const match = matches.find(m => m.id === matchId)
      if (!match || !match.deathmatch_scores) return

      // Prepare scores array from editing state
      const scores = match.deathmatch_scores.map(scoreData => ({
        participant_id: scoreData.participant_id,
        score: editingScores[matchId]?.[scoreData.participant_id] ?? scoreData.score
      }))

      await tournamentsAPI.updateDeathmatchScores(id, matchId, scores)

      // Clear editing state for this match
      setEditingScores(prev => {
        const newState = { ...prev }
        delete newState[matchId]
        return newState
      })

      // Refresh tournament data
      await fetchTournament()
    } catch (err) {
      console.error('Failed to save deathmatch scores:', err)
      setError(err.response?.data?.error || 'Failed to save scores')
    } finally {
      setSavingScores(null)
    }
  }

  // Go to next round
  const handleGoToNextRound = (currentMatchId) => {
    const currentMatch = matches.find(m => m.id === currentMatchId)
    if (!currentMatch) return

    // Find the next round
    const nextMatch = matches.find(m => m.round === currentMatch.round + 1)
    if (nextMatch) {
      // Close current round and open next
      setOpenRounds(prev => ({
        ...prev,
        [currentMatchId]: false,
        [nextMatch.id]: true
      }))

      // Scroll to next round (optional, for better UX)
      setTimeout(() => {
        const nextRoundElement = document.getElementById(`round-${nextMatch.id}`)
        if (nextRoundElement) {
          nextRoundElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }

  // Toggle round accordion
  const toggleRound = (matchId) => {
    setOpenRounds(prev => ({
      ...prev,
      [matchId]: !prev[matchId]
    }))
  }

  // Fetch match schedule
  const fetchMatchSchedule = async () => {
    try {
      const response = await statsAPI.getMatchSchedule(id)
      setMatchSchedule(response.data.matches || [])
    } catch (err) {
      console.error('Failed to fetch match schedule:', err)
    }
  }

  // useEffect to fetch schedule when tournament loads
  useEffect(() => {
    if (tournament && tournament.status !== 'registration') {
      fetchMatchSchedule()
    }
  }, [tournament?.id, tournament?.status])

  // Handle edit schedule
  const handleEditSchedule = (match) => {
    setEditingSchedule(match.id)
    // Convert to datetime-local format if scheduled_time exists
    if (match.scheduled_time) {
      const date = new Date(match.scheduled_time)
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
      setScheduleTime(localDateTime)
    } else {
      setScheduleTime('')
    }
  }

  // Handle save schedule
  const handleSaveSchedule = async (matchId) => {
    if (!scheduleTime) {
      alert('Please select a date and time')
      return
    }

    try {
      setUpdatingSchedule(true)
      await statsAPI.updateMatchSchedule(id, matchId, {
        scheduled_time: new Date(scheduleTime).toISOString()
      })
      await fetchMatchSchedule()
      setEditingSchedule(null)
      setScheduleTime('')
    } catch (err) {
      console.error('Failed to update schedule:', err)
      alert(err.response?.data?.error || 'Failed to update match schedule')
    } finally {
      setUpdatingSchedule(false)
    }
  }

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end for participant seeding
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = participants.findIndex((p) => p.id === active.id)
      const newIndex = participants.findIndex((p) => p.id === over.id)

      const newParticipants = arrayMove(participants, oldIndex, newIndex)
      setParticipants(newParticipants)

      // Update seeding on backend
      updateParticipantSeeding(newParticipants)
    }
  }

  // Update participant seeding order on backend
  const updateParticipantSeeding = async (orderedParticipants) => {
    try {
      const seedingData = orderedParticipants.map((p, index) => ({
        id: p.id,
        seed: index + 1
      }))

      await tournamentsAPI.updateParticipantSeeding(id, seedingData)
    } catch (err) {
      console.error('Failed to update seeding:', err)
      // Silently fail - user can manually refresh if needed
    }
  }

  // Open first round by default when matches load
  useEffect(() => {
    if (matches.length > 0 && tournament?.type === 'deathmatch') {
      const firstMatch = matches[0]
      if (firstMatch && !openRounds[firstMatch.id]) {
        setOpenRounds({ [firstMatch.id]: true })
      }
    }
  }, [matches, tournament?.type])

  const isAdmin = ['admin','superadmin'].includes(currentUser?.role) || currentUser?.role === 'referee'
  const needsFormat = tournament?.type === 'pending'
  const canStart = isAdmin && tournament?.status === 'registration' && participants.length >= 2 && !needsFormat
  const canCancel = isAdmin && tournament?.status !== 'completed' && tournament?.status !== 'cancelled'
  const canDelete = ['admin','superadmin'].includes(currentUser?.role)

  // Calculate overall standings for score-based tournaments (deathmatch + FFA).
  // Beide gebruiken deathmatch_scores per match/heat; klassement = cumulatief
  // over alle heats/rondes, gesorteerd op totaalscore desc.
  const calculateOverallStandings = () => {
    if (!(tournament?.type === 'deathmatch' || tournament?.type === 'ffa') || !matches.length) return []

    // Create a map to store total scores per participant
    const participantTotals = new Map()

    // Sum up scores from all rounds
    matches.forEach(match => {
      if (match.deathmatch_scores) {
        match.deathmatch_scores.forEach(scoreData => {
          const current = participantTotals.get(scoreData.participant_id) || {
            participant_id: scoreData.participant_id,
            team_name: scoreData.team_name,
            username: scoreData.username,
            total_score: 0,
            rounds_played: 0
          }
          current.total_score += scoreData.score || 0
          current.rounds_played += 1
          participantTotals.set(scoreData.participant_id, current)
        })
      }
    })

    // Convert to array and sort by total score
    const standings = Array.from(participantTotals.values())
      .sort((a, b) => b.total_score - a.total_score)
      .map((standing, index) => ({
        ...standing,
        placement: index + 1
      }))

    return standings
  }

  const overallStandings = calculateOverallStandings()

  // Sortable Participant Item Component
  const SortableParticipantItem = ({ participant, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: participant.id })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }

    const canDrag = isAdmin && tournament?.status === 'registration'

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex flex-col gap-2 p-3 rounded-lg border border-border bg-card/80 ${
          isDragging ? 'shadow-lg ring-2 ring-acid' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {canDrag && (
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing hover:text-acid transition-colors"
              >
                <GripVertical className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <Badge variant="outline" className="text-xs">
              #{participant.seed || index + 1}
            </Badge>
            <span className="font-medium">{participant.team_name}</span>
            {participant.username && !participant.team_members?.length && (
              <span className="text-sm text-muted-foreground">
                ({participant.username})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {participant.no_show && (
              <Badge variant="outline" className="bg-slate-500/20 text-slate-400"
                     title="Deelnemer is als no-show gemarkeerd — wordt uitgesloten bij bracket-start">
                no-show
              </Badge>
            )}
            {participant.eliminated && (
              <Badge variant="outline" className="bg-red-500/20 text-red-400">
                Uitgeschakeld
              </Badge>
            )}
            {/* Participant-niveau no-show — voor pre-emptief "hij komt niet"
                of om per ongeluk gemarkeerde deelnemer terug te zetten.
                Bij voorkeur gebruik je de no-show-knop in Toernooi-regie
                op de actieve match zelf (want dat pusht ook meteen de
                tegenstander door als winner). */}
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const started = tournament?.status && tournament.status !== 'registration'
                  const msg = participant.no_show
                    ? 'Deze speler terug in het toernooi zetten? Al afgesloten forfeits (NO-SHOW/NO-CONTEST) worden NIET automatisch teruggerold.'
                    : (started
                        ? 'Deze speler diskwalificeren?\n\nAlle openstaande matches waarin hij speelt worden meteen afgehandeld:\n• Tegenstander wint (NO-SHOW) en schuift door in de bracket.\n• Als beide spelers al gediskwalificeerd zijn wordt de match NO-CONTEST.\n• FFA-heats: deelnemer wordt uit de heat gehaald, rest speelt door.'
                        : 'Deze speler nu al uit het toernooi zetten (no-show)?')
                  if (!window.confirm(msg)) return
                  try {
                    const r = await tournamentsAPI.adminMarkNoShow(tournament.id, participant.id, !participant.no_show)
                    const c = r?.data?.cascade
                    if (c && (c.forfeits || c.no_contests || c.skipped)) {
                      window.alert(`Cascade uitgevoerd:\n• ${c.forfeits} match(es) → NO-SHOW (tegenstander wint)\n• ${c.no_contests} match(es) → NO-CONTEST\n• ${c.skipped} match(es) overgeslagen (leeg slot / FFA-heat)`)
                    }
                    fetchTournament()
                  } catch (e) { window.alert(e.response?.data?.error || e.message) }
                }}
                title={participant.no_show
                  ? 'Weer actief in het toernooi'
                  : (tournament?.status === 'registration'
                      ? 'Uitschakelen — vervalt voor rest van toernooi'
                      : 'Diskwalificeer — openstaande matches auto-forfeit + speler uit stand')}
                className="text-xs"
              >
                {participant.no_show
                  ? 'Terugzetten'
                  : (tournament?.status === 'registration' ? 'Uitschakelen' : 'Diskwalificeer')}
              </Button>
            )}
            {/* Remove button - only during registration phase */}
            {isAdmin && tournament?.status === 'registration' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRemoveParticipant(participant.id, participant.team_name)}
                disabled={removingParticipant === participant.id}
                className="border-red-500 text-red-500 hover:bg-red-500/20"
              >
                {removingParticipant === participant.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <UserMinus className="h-3 w-3 mr-1" />
                    Remove
                  </>
                )}
              </Button>
            )}
            {/* Mark as given up - only during in_progress phase */}
            {isAdmin && tournament?.status === 'in_progress' && !participant.eliminated && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMarkGivenUp(participant.id, participant.team_name)}
                disabled={markingGivenUp === participant.id}
                className="border-orange-500 text-orange-500 hover:bg-orange-500/20"
              >
                {markingGivenUp === participant.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Flag className="h-3 w-3 mr-1" />
                    Give Up
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        {/* Show team members for team tournaments */}
        {participant.team_members && participant.team_members.length > 0 && (
          <div className="ml-8 space-y-1">
            <div className="text-xs text-muted-foreground font-medium">Team Members:</div>
            {participant.team_members.map((member) => (
              <div key={member.user_id} className="text-sm text-muted-foreground flex items-center gap-2">
                <Users className="h-3 w-3" />
                <span>{member.display_name || member.username}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-acid" />
      </div>
    )
  }

  if (error && !tournament) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/tournaments')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tournaments
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header — flex-wrap zodat op mobiel de actiebalk onder de titel
          landt ipv buiten viewport valt. */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Button
            variant="ghost"
            onClick={() => navigate('/tournaments')}
            className="hover:bg-card shrink-0"
          >
            <ArrowLeft className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Terug</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 min-w-0">
              <Trophy className="h-6 w-6 md:h-8 md:w-8 shrink-0" />
              <span className="truncate">{tournament?.name}</span>
            </h1>
            <p className="text-muted-foreground text-sm truncate">{tournament?.game}</p>
          </div>
        </div>

        {/* Action Buttons — primaire actie apart, destructieve acties achter
            een "Meer"-dropdown zodat Restart/Cancel/Delete niet meer één-klik
            weg zijn. */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchTournament}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Ververs
          </Button>

          {isAdmin && tournament && (
            <Button variant="outline" onClick={handleOpenEditDialog}>
              <Edit className="h-4 w-4 mr-2" />
              Bewerken
            </Button>
          )}

          {canStart && (
            <Button onClick={handleStartTournament} disabled={starting}>
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Start toernooi
            </Button>
          )}

          {isAdmin && tournament?.status === 'completed' && (
            <Button variant="outline"
                    onClick={() => { setAwardDialogOpen(true); setAwardMsg(null) }}>
              <Award className="h-4 w-4 mr-2" />
              Punten uitkeren
            </Button>
          )}

          {isAdmin && (canDelete || canCancel || tournament?.status === 'in_progress') && (
            <MeerActies
              onRestart={tournament?.status === 'in_progress' ? handleRestartTournament : null}
              onCancel={canCancel ? handleCancelTournament : null}
              onDelete={canDelete ? handleDeleteTournament : null}
              restarting={restarting} canceling={canceling} deleting={deleting} />
          )}
        </div>
      </div>

      {isAdmin && needsFormat && tournament?.status === 'registration' && (
        <TournamentFormatPicker
          tournament={tournament}
          participants={participants}
          onChosen={fetchTournament} />
      )}

      {/* Points award dialog */}
      <Dialog open={awardDialogOpen} onOpenChange={setAwardDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-400" />
              Punten uitkeren
            </DialogTitle>
            <p className="text-sm text-muted-foreground">Stel punten in per plaatsing voor <span className="font-medium">{tournament?.name}</span></p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 1,             label: '🥇 1e plaats' },
              { key: 2,             label: '🥈 2e plaats' },
              { key: 3,             label: '🥉 3e plaats' },
              { key: 'participation', label: '🎮 Deelname (overige)' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <span className="text-sm flex-1">{label}</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number" min="0"
                    value={awardPoints[key] ?? 0}
                    onChange={e => setAwardPoints(p => ({ ...p, [key]: e.target.value }))}
                    className="w-20 bg-background border border-border rounded px-2 py-1 text-sm text-center focus:outline-none focus:border-yellow-500/50"
                  />
                  <span className="text-xs text-muted-foreground">pt</span>
                </div>
              </div>
            ))}
            <div className="border-t border-border pt-3 text-xs text-muted-foreground space-y-1">
              {participants.filter(p => p.user_id || p.team_members?.length).map(p => (
                <div key={p.id} className="flex justify-between">
                  <span>{p.team_name}</span>
                  <span className="text-yellow-400">
                    {p.placement === 1 ? awardPoints[1] : p.placement === 2 ? awardPoints[2] : p.placement === 3 ? awardPoints[3] : awardPoints.participation || 0} pt
                  </span>
                </div>
              ))}
            </div>
            {awardMsg && (
              <p className={`text-xs px-2 py-1 rounded ${awardMsg.type === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                {awardMsg.text}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAwardDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleAwardPoints} disabled={awarding}
              className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/40">
              {awarding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Bezig…</> : 'Punten toekennen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tournament Info */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-acid" />
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-bold capitalize">{tournament?.status?.replace('_', ' ')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Trophy className="h-5 w-5 text-acid" />
              <div>
                <p className="text-sm text-muted-foreground">Format</p>
                <p className="font-bold capitalize">
                  {tournament?.type === 'pending'
                    ? <span className="text-signal">Nog te kiezen</span>
                    : tournament?.type?.replace('_', ' ')}
                  {tournament?.type && tournament.type !== 'pending' && (
                    <Link to={`/wiki/${wikiSlugForType(tournament.type)}`}
                          title={`Wat is ${tournament.type}?`}
                          className="ml-2 text-xs font-normal text-acid hover:underline">
                      ?
                    </Link>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-acid" />
              <div>
                <p className="text-sm text-muted-foreground">Deelnemers</p>
                <p className="font-bold">
                  {participants.length}
                  {tournament?.max_participants && ` / ${tournament.max_participants}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Award className="h-5 w-5 text-acid" />
              <div>
                <p className="text-sm text-muted-foreground">Matches</p>
                <p className="font-bold">{matches.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Mobile: horizontaal scrollen ipv grid squish. Desktop (md+):
            grid van gelijke kolommen. Op smalle schermen was elke tab-label
            afgekapt tot 2-3 karakters. */}
        <div className="overflow-x-auto md:overflow-visible -mx-4 md:mx-0 px-4 md:px-0">
          <TabsList className="w-max md:w-full md:max-w-4xl md:grid" style={{ gridTemplateColumns: `repeat(${6 + (tournament?.features?.predictions ? 1 : 0) + (isAdmin ? 2 : 0)}, minmax(0, 1fr))` }}>
            <TabsTrigger value="bracket">Bracket</TabsTrigger>
            <TabsTrigger value="standings">Klassement</TabsTrigger>
            <TabsTrigger value="participants">Deelnemers</TabsTrigger>
            <TabsTrigger value="schedule">Schema</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="rules">Regels</TabsTrigger>
            {tournament?.features?.predictions && <TabsTrigger value="predictions">Predicties</TabsTrigger>}
            {isAdmin && <TabsTrigger value="settings">Instellingen</TabsTrigger>}
            {isAdmin && (
              <TabsTrigger value="uitreiking">
                Uitreiking
                {tournament && tournament.status === 'completed' && (() => {
                  // Alleen 'wacht'-dot tonen als er echt iets geconfigureerd is
                  // maar nog niet uitgereikt. Zonder deze check bleef de dot
                  // eeuwig branden bij toernooien zonder punten/badge/prijs.
                  const hasP = tournament.points_reward &&
                    (typeof tournament.points_reward === 'object'
                      ? Object.keys(tournament.points_reward).length > 0
                      : String(tournament.points_reward).length > 0)
                  const wacht =
                    (hasP && !tournament.points_awarded_at) ||
                    (tournament.badge_type_id && !tournament.badges_awarded_at) ||
                    (tournament.prize_id && !tournament.prize_awarded_at)
                  return wacht ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-peach inline-block" /> : null
                })()}
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Bracket Tab */}
        <TabsContent value="bracket" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {tournament?.type === 'deathmatch' ? 'Deathmatch-scores' : 'Toernooi-bracket'}
              </CardTitle>
              <CardDescription>
                {tournament?.status === 'registration'
                  ? 'Start het toernooi om de bracket te genereren'
                  : tournament?.type === 'deathmatch'
                  ? (isAdmin ? 'Vul per ronde de scores in per deelnemer' : 'Live-scores per ronde')
                  : (isAdmin ? 'Klik op een match om het resultaat in te vullen' : 'Klik op een match voor scores en games')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tournament?.type === 'deathmatch' ? (
                <div className="space-y-6">
                  {matches.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">
                      Nog geen matches — worden zichtbaar zodra de organisator het toernooi start.
                    </p>
                  ) : (
                    <>
                      {/* Overall Standings */}
                      {overallStandings.length > 0 && (
                        <Card className="border-yellow-500/30 bg-gradient-to-br from-yellow-900/10 to-transparent">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-yellow-500">
                              <Trophy className="h-5 w-5" />
                              Overall Standings
                            </CardTitle>
                            <CardDescription>
                              Total scores across all {tournament?.rounds || 1} rounds
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {overallStandings.slice(0, 10).map((standing, index) => (
                                <div
                                  key={standing.participant_id}
                                  className={`flex items-center gap-4 p-3 rounded-lg ${
                                    standing.placement === 1
                                      ? 'bg-yellow-500/20 border border-yellow-500/50'
                                      : standing.placement === 2
                                      ? 'bg-muted/40 border border-muted-foreground/40'
                                      : standing.placement === 3
                                      ? 'bg-orange-500/20 border border-orange-500/50'
                                      : 'bg-card/60 border border-border'
                                  }`}
                                >
                                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-card">
                                    {standing.placement === 1 && <span className="text-2xl">🥇</span>}
                                    {standing.placement === 2 && <span className="text-2xl">🥈</span>}
                                    {standing.placement === 3 && <span className="text-2xl">🥉</span>}
                                    {standing.placement > 3 && (
                                      <span className="text-lg font-bold text-muted-foreground">
                                        #{standing.placement}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold">{standing.team_name}</div>
                                    {standing.username && (
                                      <div className="text-sm text-muted-foreground">
                                        {standing.username}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-yellow-500">
                                      {standing.total_score}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {standing.rounds_played} rounds
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Individual Rounds */}
                      {matches.map((match) => {
                      const isOpen = openRounds[match.id]
                      const highestScore = match.deathmatch_scores?.[0]?.score || 0
                      const leaderName = match.deathmatch_scores?.[0]?.team_name || 'TBD'

                      return (
                        <Card key={match.id} id={`round-${match.id}`} className="border-border">
                          <CardHeader
                            className="cursor-pointer hover:bg-card/60 transition-colors"
                            onClick={() => toggleRound(match.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {isOpen ? (
                                  <ChevronDown className="h-5 w-5 text-neon-pink" />
                                ) : (
                                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                                )}
                                <CardTitle className="text-lg">
                                  Round {match.round}
                                </CardTitle>
                                {!isOpen && match.deathmatch_scores && match.deathmatch_scores.length > 0 && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Award className="h-4 w-4 text-yellow-500" />
                                    <span>{leaderName}</span>
                                    <span className="text-xs">({highestScore})</span>
                                  </div>
                                )}
                              </div>
                              {isAdmin && match.deathmatch_scores && isOpen && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleSaveScores(match.id)
                                    }}
                                    disabled={savingScores === match.id}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    {savingScores === match.id ? (
                                      <>
                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                        Saving...
                                      </>
                                    ) : (
                                      'Save Scores'
                                    )}
                                  </Button>
                                  {(() => {
                                    // Check if this is not the last round
                                    const isLastRound = match.round >= (tournament?.rounds || 1)
                                    if (!isLastRound) {
                                      return (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            handleGoToNextRound(match.id)
                                          }}
                                          className="border-acid text-acid hover:bg-acid/20"
                                        >
                                          <ChevronRight className="h-4 w-4 mr-1" />
                                          Next Round
                                        </Button>
                                      )
                                    }
                                    return null
                                  })()}
                                </div>
                              )}
                            </div>
                          </CardHeader>
                          {isOpen && (
                            <CardContent>
                          {match.deathmatch_scores && match.deathmatch_scores.length > 0 ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-12 gap-2 pb-2 border-b border-border text-sm font-medium text-muted-foreground">
                                <div className="col-span-1">#</div>
                                <div className="col-span-6">Participant</div>
                                <div className="col-span-3">Score</div>
                                <div className="col-span-2">Rank</div>
                              </div>
                              {match.deathmatch_scores.map((scoreData, index) => {
                                const currentScore = editingScores[match.id]?.[scoreData.participant_id] ?? scoreData.score
                                return (
                                  <div
                                    key={scoreData.id}
                                    className="grid grid-cols-12 gap-2 items-center p-2 rounded border border-border bg-card/80"
                                  >
                                    <div className="col-span-1 text-muted-foreground">
                                      {index + 1}
                                    </div>
                                    <div className="col-span-6 font-medium">
                                      {scoreData.team_name}
                                      {scoreData.username && (
                                        <span className="text-sm text-muted-foreground ml-2">
                                          ({scoreData.username})
                                        </span>
                                      )}
                                    </div>
                                    <div className="col-span-3">
                                      {isAdmin ? (
                                        <input
                                          type="number"
                                          value={currentScore}
                                          onChange={(e) =>
                                            handleScoreChange(
                                              match.id,
                                              scoreData.participant_id,
                                              parseInt(e.target.value) || 0
                                            )
                                          }
                                          className="w-full px-2 py-1 rounded border border-border bg-card text-white focus:outline-none focus:ring-2 focus:ring-acid"
                                          min="0"
                                        />
                                      ) : (
                                        <span className="text-lg font-bold">{currentScore}</span>
                                      )}
                                    </div>
                                    <div className="col-span-2">
                                      {scoreData.placement ? (
                                        <Badge
                                          variant="outline"
                                          className={
                                            scoreData.placement === 1
                                              ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500'
                                              : scoreData.placement === 2
                                              ? 'bg-muted/40 text-muted-foreground border-muted-foreground'
                                              : scoreData.placement === 3
                                              ? 'bg-orange-500/20 text-orange-400 border-orange-500'
                                              : 'bg-card/40 text-muted-foreground border-border'
                                          }
                                        >
                                          {scoreData.placement === 1 && '🥇'}
                                          {scoreData.placement === 2 && '🥈'}
                                          {scoreData.placement === 3 && '🥉'}
                                          {scoreData.placement > 3 && `#${scoreData.placement}`}
                                        </Badge>
                                      ) : (
                                        <span className="text-muted-foreground">-</span>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <p className="text-center py-4 text-muted-foreground">
                              No scores available yet
                            </p>
                          )}
                        </CardContent>
                          )}
                        </Card>
                      )
                    })}
                    </>
                  )}
                </div>
              ) : (
                <TournamentBracket
                  matches={matches}
                  onMatchClick={isAdmin ? handleMatchClick : null}
                  isAdmin={isAdmin}
                  tournamentType={tournament?.type}
                  tournamentId={tournament?.id}
                  participants={participants}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Standings Tab */}
        <TabsContent value="standings">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                <CardTitle>Overall Standings</CardTitle>
              </div>
              <CardDescription>
                {tournament?.type === 'deathmatch' || tournament?.type === 'ffa'
                  ? `Cumulatief klassement over alle ${tournament?.rounds || 1} rondes (som van scores)`
                  : 'Eindstand op basis van de match-resultaten van dit toernooi'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(tournament?.type === 'deathmatch' || tournament?.type === 'ffa') ? (
                overallStandings.length > 0 ? (
                  <div className="space-y-3">
                    {overallStandings.map((standing) => (
                      <div
                        key={standing.participant_id}
                        className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                          standing.placement === 1
                            ? 'bg-yellow-500/20 border-2 border-yellow-500/50 shadow-lg shadow-yellow-500/20'
                            : standing.placement === 2
                            ? 'bg-muted/40 border-2 border-muted-foreground/40'
                            : standing.placement === 3
                            ? 'bg-orange-500/20 border-2 border-orange-500/50'
                            : 'bg-card/60 border border-border'
                        }`}
                      >
                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-card border-2 border-border">
                          {standing.placement === 1 && <span className="text-3xl">🥇</span>}
                          {standing.placement === 2 && <span className="text-3xl">🥈</span>}
                          {standing.placement === 3 && <span className="text-3xl">🥉</span>}
                          {standing.placement > 3 && (
                            <span className="text-xl font-bold text-muted-foreground">
                              #{standing.placement}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-lg font-bold">{standing.team_name}</div>
                          {standing.username && (
                            <div className="text-sm text-muted-foreground">
                              {standing.username}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            {standing.rounds_played} rounds played
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-yellow-500">
                            {standing.total_score}
                          </div>
                          <div className="text-xs text-muted-foreground uppercase tracking-wide">
                            Total Score
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Nog geen klassement — zichtbaar zodra het toernooi loopt.
                    </p>
                  </div>
                )
              ) : (
                <GenericStandings tournament={tournament} participants={participants} matches={matches} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants">
          <MyCheckInCard tournament={tournament} participants={participants} currentUser={currentUser} onSaved={fetchTournament} />
          <MyMatchReadyCard tournament={tournament} matches={matches} participants={participants} currentUser={currentUser} onSaved={fetchTournament} />

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle>Deelnemers ({participants.length})</CardTitle>
                  <CardDescription>
                    Alle ingeschreven deelnemers voor dit toernooi
                    {isAdmin && tournament?.status === 'registration' && (
                      <span className="block mt-1 text-acid">
                        Sleep om de seeding aan te passen
                      </span>
                    )}
                  </CardDescription>
                </div>
                {isAdmin && tournament?.status === 'registration' && tournament?.registration_type === 'personal' && (
                  <Button size="sm" variant="outline" onClick={() => { setWalkInOpen(true); setWalkInQ(''); setWalkInList([]) }}>
                    <Users className="h-4 w-4 mr-1" /> Deelnemer toevoegen
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {participants.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nog geen deelnemers
                </p>
              ) : isAdmin && tournament?.status === 'registration' ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={participants.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {participants.map((participant, index) => (
                        <SortableParticipantItem
                          key={participant.id}
                          participant={participant}
                          index={index}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="space-y-2">
                  {participants.map((participant, index) => (
                    <SortableParticipantItem
                      key={participant.id}
                      participant={participant}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-acid" />
                <CardTitle>Match Schedule</CardTitle>
              </div>
              <CardDescription>
                View and manage when matches are scheduled to be played
              </CardDescription>
            </CardHeader>
            <CardContent>
              {matchSchedule.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No matches scheduled yet</p>
                  {tournament?.status === 'registration' && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Bracket wordt zichtbaar zodra het toernooi start
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {matchSchedule.map((match) => {
                    const now = new Date()
                    const scheduledTime = match.scheduled_time ? new Date(match.scheduled_time) : null
                    const isUpcoming = scheduledTime && scheduledTime > now && match.status === 'pending'
                    const isOngoing = match.status === 'in_progress'
                    const isCompleted = match.status === 'completed'
                    const canEdit = currentUser && (['admin','superadmin'].includes(currentUser?.role) || currentUser.role === 'referee')

                    return (
                      <div
                        key={match.id}
                        className={`p-4 rounded-lg border ${
                          isOngoing
                            ? 'bg-yellow-500/10 border-yellow-500/50'
                            : isUpcoming
                            ? 'bg-acid/10 border-acid/50'
                            : isCompleted
                            ? 'bg-green-500/10 border-green-500/50'
                            : 'bg-muted/30 border-muted'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="font-mono">
                                Round {match.round} - Match {match.match_number}
                              </Badge>
                              {isOngoing && (
                                <Badge className="bg-yellow-500 hover:bg-yellow-600">
                                  In Progress
                                </Badge>
                              )}
                              {isUpcoming && (
                                <Badge className="bg-acid hover:bg-acid">
                                  Upcoming
                                </Badge>
                              )}
                              {isCompleted && (
                                <Badge className="bg-green-500 hover:bg-green-600">
                                  Completed
                                </Badge>
                              )}
                            </div>

                            <div className="text-sm space-y-1">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span>
                                  {/* FFA/deathmatch-heats hebben geen participant1/2 — de
                                      deelnemer-namen komen uit deathmatch_scores. */}
                                  {(match.bracket_type === 'ffa' || match.bracket_type === 'deathmatch') && match.deathmatch_scores?.length
                                    ? match.deathmatch_scores.map(s => s.team_name || s.username || `#${s.participant_id}`).join(', ')
                                    : `${match.participant1_name || 'TBD'} vs ${match.participant2_name || 'TBD'}`}
                                </span>
                              </div>

                              {editingSchedule === match.id ? (
                                <div className="mt-3 space-y-2">
                                  <Label htmlFor={`schedule-${match.id}`}>Scheduled Time</Label>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      id={`schedule-${match.id}`}
                                      type="datetime-local"
                                      value={scheduleTime}
                                      onChange={(e) => setScheduleTime(e.target.value)}
                                      className="flex-1"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveSchedule(match.id)}
                                      disabled={updatingSchedule}
                                    >
                                      {updatingSchedule ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        'Save'
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingSchedule(null)
                                        setScheduleTime('')
                                      }}
                                      disabled={updatingSchedule}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 mt-2">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">
                                    {scheduledTime
                                      ? scheduledTime.toLocaleString('nl-NL', {
                                          weekday: 'short',
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      : 'Not scheduled yet'}
                                  </span>
                                  {canEdit && !isCompleted && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEditSchedule(match)}
                                      className="ml-2 h-7 px-2"
                                    >
                                      <Edit className="h-3 w-3 mr-1" />
                                      {scheduledTime ? 'Edit' : 'Set Time'}
                                    </Button>
                                  )}
                                </div>
                              )}

                              {match.winner_name && (
                                <div className="flex items-center gap-2 text-green-400 mt-2">
                                  <Trophy className="h-4 w-4" />
                                  <span>Winner: {match.winner_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="media" className="space-y-4">
          <MediaTab tournamentId={tournament?.id} />
        </TabsContent>

        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-neon-pink" />
                <CardTitle>Tournament Rules & Format</CardTitle>
              </div>
              <CardDescription>
                Game rules, scoring system, and format information
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tournament?.rules ? (
                <div className="prose prose-invert max-w-none">
                  <div className="whitespace-pre-wrap text-sm bg-card/80 p-4 rounded-lg border border-border">
                    {tournament.rules}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Voor dit toernooi zijn nog geen regels vastgelegd.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {tournament?.features?.predictions && (
          <TabsContent value="predictions" className="space-y-4">
            <PredictionsTab tournament={tournament} matches={matches} participants={participants} currentUser={currentUser} onRefresh={fetchTournament} />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="uitreiking" className="space-y-4">
            <UitreikingPanel tournament={tournament} onDone={fetchTournament} />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="settings" className="space-y-4">
            <StageControlPanel tournament={tournament} matches={matches} participants={participants} />
            {/* key op tournament-type: als admin het format wijzigt via de
                picker, forceert dit een re-mount van de Features-tab zodat de
                format-specifieke instellingen direct verschijnen. */}
            <TournamentFeaturesTab key={tournament?.type || 'pending'}
                                   tournament={tournament} onSaved={fetchTournament} />
            <PrizeCouplingCard tournament={tournament} participants={participants} onSaved={fetchTournament} />
            <PointsRewardCard tournament={tournament} participants={participants} onSaved={fetchTournament} />
            <BadgeCouplingCard tournament={tournament} participants={participants} onSaved={fetchTournament} />
            <CheckInAdminTab tournament={tournament} participants={participants} onSaved={fetchTournament} />
          </TabsContent>
        )}
      </Tabs>

      {/* Walk-in registration dialog — admin schrijft een user direct in. */}
      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Deelnemer toevoegen</DialogTitle>
            <DialogDescription>
              Voor laatkomers of walk-ins — de user zelf hoeft niet in te loggen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input autoFocus value={walkInQ}
                   onChange={e => setWalkInQ(e.target.value)}
                   placeholder="Zoek user…" />
            <div className="max-h-[24rem] overflow-y-auto space-y-1">
              {walkInList
                .filter(u => !participants.some(p => p.user_id === u.id))
                .slice(0, 20)
                .map(u => (
                  <button key={u.id} type="button"
                          disabled={walkInBusy}
                          onClick={async () => {
                            setWalkInBusy(true)
                            try {
                              await tournamentsAPI.joinTournament(id, {
                                team_name: u.display_name || u.username,
                                user_id: u.id,
                              })
                              setWalkInOpen(false)
                              await fetchTournament()
                            } catch (e) {
                              setError(e.response?.data?.error || e.message)
                            } finally {
                              setWalkInBusy(false)
                            }
                          }}
                          className="w-full flex items-center justify-between rounded border border-border bg-card px-3 py-2 text-left hover:border-acid transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{u.display_name || u.username}</div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate">@{u.username}</div>
                    </div>
                    <span className="text-xs text-acid">Voeg toe</span>
                  </button>
                ))}
              {!walkInBusy && walkInQ && walkInList.filter(u => !participants.some(p => p.user_id === u.id)).length === 0 && (
                <p className="text-xs text-muted-foreground p-2">Geen (nog niet ingeschreven) gebruiker gevonden.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tournament Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] border-acid/30">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle className="text-acid">Toernooi bewerken</DialogTitle>
              <DialogDescription>
                Werk toernooi-details bij. Let op: format, inschrijvings-modus, scoring-type en rondes kunnen niet meer gewijzigd worden na aanmaak.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {editError && (
                <Alert variant="destructive">
                  <AlertDescription>{editError}</AlertDescription>
                </Alert>
              )}

              <div className="grid gap-2">
                <Label htmlFor="edit-name">Tournament Name *</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => handleEditChange('name', e.target.value)}
                  placeholder="Summer Championship 2025"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-game">Game *</Label>
                <Input
                  id="edit-game"
                  value={editForm.game}
                  onChange={(e) => handleEditChange('game', e.target.value)}
                  placeholder="Counter-Strike 2"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-max-participants">Max Participants</Label>
                <Input
                  id="edit-max-participants"
                  type="number"
                  value={editForm.max_participants}
                  onChange={(e) => handleEditChange('max_participants', e.target.value)}
                  placeholder="16"
                  min="2"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-start-time">Start Time</Label>
                <Input
                  id="edit-start-time"
                  type="datetime-local"
                  value={editForm.start_time}
                  onChange={(e) => handleEditChange('start_time', e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-description">Description</Label>
                <textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => handleEditChange('description', e.target.value)}
                  placeholder="Tournament details, prizes, schedule..."
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-rules">Rules & Format</Label>
                <textarea
                  id="edit-rules"
                  value={editForm.rules}
                  onChange={(e) => handleEditChange('rules', e.target.value)}
                  placeholder="Format: Best of 3&#10;Scoring: 1 point per kill&#10;Map Pool: Dust2, Mirage, Inferno&#10;Time Limit: 15 minutes per round"
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <p className="text-xs text-muted-foreground">
                  Define tournament format, scoring system, game settings, and any special rules
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditDialogOpen(false)}
                disabled={editLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={editLoading}
                className="bg-acid hover:bg-acid/90"
              >
                {editLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Match Result Dialog */}
      <MatchResultDialog
        open={matchDialogOpen}
        onClose={() => setMatchDialogOpen(false)}
        match={selectedMatch}
        onSubmit={handleUpdateMatch}
        loading={updatingMatch}
      />
      {/* FFA per-heat score-invoer */}
      <FFAScoreDialog
        open={ffaHeatOpen}
        onClose={() => setFfaHeatOpen(false)}
        match={selectedMatch}
        onSubmit={handleFFAScoreSubmit}
        loading={updatingMatch}
      />
    </div>
  )
}

/* ==================================================================
   Tournament Features toggles (track A/B/C/D opt-in)
================================================================== */
function TournamentFeaturesTab({ tournament, onSaved }) {
  const [saving, setSaving] = useState(false)
  const features = tournament?.features || {}

  const toggle = async (key) => {
    setSaving(true)
    try {
      await tournamentsAPI.updateFeatures(tournament.id, { [key]: !features[key] })
      await onSaved()
    } finally { setSaving(false) }
  }

  const setValue = async (key, value) => {
    setSaving(true)
    try {
      await tournamentsAPI.updateFeatures(tournament.id, { [key]: value })
      await onSaved()
    } finally { setSaving(false) }
  }

  const started = tournament?.status !== 'registration'

  const FEATURES = [
    { key: 'check_in',           label: 'Check-in vereist',      hint: 'Deelnemers moeten zich inchecken voor de start.' },
    { key: 'ready_check',        label: 'Ready-check per match', hint: 'Beide spelers klikken "ready" voor match start.' },
    { key: 'beamer',             label: 'Beamer view',           hint: 'Live bracket op grote scherm.' },
    { key: 'predictions',        label: 'Predictions',           hint: 'Publiek gokt met bar-saldo op winnaars.' },
    { key: 'auto_prize',         label: 'Automatisch prijs',     hint: 'Winnaar krijgt gekoppelde prijs uit prijzenkraam.' },
    { key: 'points_award',       label: 'Punten per plaats',     hint: 'Deelnemers krijgen automatisch punten volgens plaatsing.' },
    { key: 'badges',             label: 'Badges',                hint: 'Winnaar-badge zichtbaar naast naam.' },
    { key: 'auto_score_cs',      label: 'CS auto-score',         hint: 'Match-resultaat automatisch uit CS-server logs.' },
    { key: 'third_place_match',  label: '3e-plaats match',       hint: 'Extra match tussen halve-finale verliezers. Zonder deze optie krijgen beide halve-finalisten "Halvefinalist" (gedeelde 3e).', bracketOnly: true, appliesToTypes: ['single_elimination'] },
  ]

  const bestOf = Number(features.best_of || 1)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Features</CardTitle>
        <CardDescription>Zet aan wat je voor dit toernooi wil gebruiken. Alles is optioneel.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {FEATURES.filter(f => !f.appliesToTypes || f.appliesToTypes.includes(tournament?.type)).map(f => {
          const on = !!features[f.key]
          const disabled = saving || (f.bracketOnly && started)
          return (
            <div key={f.key} className="flex items-center justify-between gap-4 py-2 border-b border-border/40 last:border-0">
              <div className="min-w-0">
                <div className="font-medium">{f.label}</div>
                <div className="text-xs text-muted-foreground">
                  {f.hint}
                  {f.bracketOnly && started && (
                    <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>
                  )}
                </div>
              </div>
              <Button size="sm" variant={on ? 'default' : 'outline'} disabled={disabled} onClick={() => toggle(f.key)}>
                {on ? 'Aan' : 'Uit'}
              </Button>
            </div>
          )
        })}

        {/* Best-of X — niet van toepassing op score-based formats (deathmatch,
            FFA): daar heeft "eerste-tot-N-games" geen betekenis, elke heat is
            één sessie met individuele scores. */}
        {tournament?.type !== 'deathmatch' && tournament?.type !== 'ffa' && (
          <div className="flex items-center justify-between gap-4 py-2">
            <div className="min-w-0">
              <div className="font-medium">Match-lengte (Best of X)</div>
              <div className="text-xs text-muted-foreground">
                1 = één game per match. 3/5/7 = eerste-tot-(N+1)/2 games wint de match.
                {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
              </div>
            </div>
            <select value={bestOf}
                    disabled={saving || started}
                    onChange={e => setValue('best_of', Number(e.target.value))}
                    className="h-9 rounded-md border border-input bg-transparent px-3 text-sm">
              <option value={1}>BO1 (één game)</option>
              <option value={3}>BO3 (eerste tot 2)</option>
              <option value={5}>BO5 (eerste tot 3)</option>
              <option value={7}>BO7 (eerste tot 4)</option>
            </select>
          </div>
        )}

        {/* Deathmatch — aantal rondes (score-based, elke ronde nieuwe heat). */}
        {tournament?.type === 'deathmatch' && (
          <div className="flex items-center justify-between gap-4 py-2">
            <div className="min-w-0">
              <div className="font-medium">Deathmatch — aantal rondes</div>
              <div className="text-xs text-muted-foreground">
                Aantal opeenvolgende rondes; scores worden cumulatief opgeteld
                voor het klassement.
                {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
              </div>
            </div>
            <input type="number" min="1" max="20"
                   defaultValue={tournament?.rounds || 1}
                   disabled={saving || started}
                   onBlur={async e => {
                     const v = Number(e.target.value) || 1
                     if (v === Number(tournament?.rounds)) return
                     try { await tournamentsAPI.updateTournament(tournament.id, { rounds: v }); onSaved?.() }
                     catch (err) { window.alert(err.response?.data?.error ?? err.message) }
                   }}
                   className="h-9 w-20 rounded-md border border-input bg-transparent px-3 text-sm text-right" />
          </div>
        )}

        {/* Swiss rondes — alleen relevant voor swiss-type. */}
        {tournament?.type === 'swiss' && (
          <div className="flex items-center justify-between gap-4 py-2">
            <div className="min-w-0">
              <div className="font-medium">Swiss — aantal rondes</div>
              <div className="text-xs text-muted-foreground">
                Vast aantal rondes; iedereen speelt telkens tegen iemand met een gelijke score.
                Leeg = automatisch (ceil(log2(N)), minimum 3).
                {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
              </div>
            </div>
            <input type="number" min="1" max="12"
                   value={features.swiss_rounds || ''}
                   placeholder="auto"
                   disabled={saving || started}
                   onChange={e => setValue('swiss_rounds', e.target.value ? Number(e.target.value) : null)}
                   className="h-9 w-20 rounded-md border border-input bg-transparent px-3 text-sm text-right" />
          </div>
        )}

        {/* Group+KO — aantal groepen + kwalificaties per groep. */}
        {tournament?.type === 'group_ko' && (
          <>
            <div className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0">
                <div className="font-medium">Aantal groepen</div>
                <div className="text-xs text-muted-foreground">
                  Deelnemers worden serpentine-verdeeld over de groepen (WK-stijl).
                  {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
                </div>
              </div>
              <input type="number" min="2" max="8"
                     value={features.group_count || ''}
                     placeholder="4"
                     disabled={saving || started}
                     onChange={e => setValue('group_count', e.target.value ? Number(e.target.value) : null)}
                     className="h-9 w-20 rounded-md border border-input bg-transparent px-3 text-sm text-right" />
            </div>
            <div className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0">
                <div className="font-medium">Kwalificaties per groep</div>
                <div className="text-xs text-muted-foreground">
                  Top-K per groep gaat door naar de knockout-fase. Standaard 2.
                  {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
                </div>
              </div>
              <input type="number" min="1" max="4"
                     value={features.qualify_per_group || ''}
                     placeholder="2"
                     disabled={saving || started}
                     onChange={e => setValue('qualify_per_group', e.target.value ? Number(e.target.value) : null)}
                     className="h-9 w-20 rounded-md border border-input bg-transparent px-3 text-sm text-right" />
            </div>
          </>
        )}

        {/* FFA — heat-grootte + aantal rondes. */}
        {tournament?.type === 'ffa' && (
          <>
            <div className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0">
                <div className="font-medium">FFA — heat-grootte</div>
                <div className="text-xs text-muted-foreground">
                  Aantal deelnemers per heat. Standaard 4. Server-cap: kies wat je game aankan.
                  {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
                </div>
              </div>
              <input type="number" min="2" max="8"
                     value={features.heat_size || ''}
                     placeholder="4"
                     disabled={saving || started}
                     onChange={e => setValue('heat_size', e.target.value ? Number(e.target.value) : null)}
                     className="h-9 w-20 rounded-md border border-input bg-transparent px-3 text-sm text-right" />
            </div>
            <div className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0">
                <div className="font-medium">FFA — aantal rondes</div>
                <div className="text-xs text-muted-foreground">
                  Hoe vaak alle deelnemers opnieuw over heats worden verdeeld.
                  Standaard 1. Meer rondes = betere cumulatieve stand.
                  {started && <span className="ml-1 text-signal">(alleen aanpasbaar vóór de start)</span>}
                </div>
              </div>
              <input type="number" min="1" max="20"
                     defaultValue={tournament?.rounds || 1}
                     disabled={saving || started}
                     onBlur={async e => {
                       const v = Number(e.target.value) || 1
                       if (v === Number(tournament?.rounds)) return
                       try { await tournamentsAPI.updateTournament(tournament.id, { rounds: v }); onSaved?.() }
                       catch (err) { window.alert(err.response?.data?.error ?? err.message) }
                     }}
                     className="h-9 w-20 rounded-md border border-input bg-transparent px-3 text-sm text-right" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   Check-in admin overview (no-show markering)
================================================================== */
function CheckInAdminTab({ tournament, participants, onSaved }) {
  const [busy, setBusy] = useState(null)
  if (!tournament?.features?.check_in) return null

  const markNoShow = async (p, flag) => {
    setBusy(p.id)
    try {
      await tournamentsAPI.adminMarkNoShow(tournament.id, p.id, flag)
      await onSaved()
    } finally { setBusy(null) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Check-in status ({participants.length})</CardTitle>
        <CardDescription>Overzicht van wie er is en wie niet komt opdagen.</CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border/40">
        {participants.map(p => (
          <div key={p.id} className="flex items-center gap-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{p.team_name}</div>
              <div className="text-xs text-muted-foreground">
                {p.no_show
                  ? <span className="text-signal">no-show</span>
                  : p.ready_at
                    ? <span className="text-live">klaar sinds {new Date(p.ready_at).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</span>
                    : p.checked_in_at
                      ? <span className="text-acid">ingecheckt {new Date(p.checked_in_at).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</span>
                      : <span className="text-muted-foreground">nog niet ingecheckt</span>
                }
              </div>
            </div>
            {!p.no_show && (
              <Button size="sm" variant="outline" disabled={busy === p.id}
                      onClick={() => markNoShow(p, true)}>
                Markeer no-show
              </Button>
            )}
            {p.no_show && (
              <Button size="sm" variant="outline" disabled={busy === p.id}
                      onClick={() => markNoShow(p, false)}>
                Terugzetten
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   Own check-in card (deelnemer self-service)
================================================================== */
function MyCheckInCard({ tournament, participants, currentUser, onSaved }) {
  const [busy, setBusy] = useState(false)
  if (!tournament?.features?.check_in || !currentUser) return null

  // Vind eigen participant (personal of via team-membership)
  const me = participants.find(p =>
    p.user_id === currentUser.id ||
    (p.team_members || []).some(tm => tm.user_id === currentUser.id)
  )
  if (!me) return null
  if (me.no_show) return null

  const doCheckIn = async () => { setBusy(true); try { await tournamentsAPI.checkIn(tournament.id); await onSaved() } finally { setBusy(false) } }
  const doReady   = async () => { setBusy(true); try { await tournamentsAPI.markReady(tournament.id); await onSaved() } finally { setBusy(false) } }

  const checkedIn = !!me.checked_in_at
  const ready = !!me.ready_at

  return (
    <Card className="mb-4" style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
      <CardContent className="p-4 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-semibold">Jouw check-in — {me.team_name}</div>
          <div className="text-xs text-muted-foreground">
            {ready ? 'Ready. De organisatie ziet dat je klaar bent voor je matches.'
                   : checkedIn ? 'Ingecheckt. Klik "Ready" als je klaar bent om te starten.'
                                : 'Nog niet ingecheckt. Meld je aan zodra je op de LAN aankomt.'}
          </div>
        </div>
        {!checkedIn && (
          <Button onClick={doCheckIn} disabled={busy}>Check in</Button>
        )}
        {checkedIn && !ready && tournament.features?.ready_check && (
          <Button onClick={doReady} disabled={busy}>Ready</Button>
        )}
        {checkedIn && !tournament.features?.ready_check && (
          <span className="text-xs text-live font-medium">✓ Ingecheckt</span>
        )}
        {ready && (
          <span className="text-xs text-live font-medium">✓ Ready</span>
        )}
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   Stage Control Panel — track A · beamer besturing
================================================================== */
function StageControlPanel({ tournament, matches, participants }) {
  const [state, setState] = useState(null)
  const [saving, setSaving] = useState(false)
  const [midiOn, setMidiOn] = useState(false)
  const [midiErr, setMidiErr] = useState('')
  const detachRef = useRef(null)

  useEffect(() => {
    if (!tournament?.id) return
    tournamentsAPI.getStageState(tournament.id).then(r => setState(r.data.state)).catch(() => {})
  }, [tournament?.id])

  const patch = async (p) => {
    setSaving(true)
    try {
      await tournamentsAPI.updateStageState(tournament.id, p)
      const r = await tournamentsAPI.getStageState(tournament.id)
      setState(r.data.state)
    } finally { setSaving(false) }
  }

  // MIDI dispatch — vertaalt actie naar patch()
  const runAction = (action) => {
    if (!action) return
    if (action.startsWith('view.')) return patch({ current_view: action.slice(5) })
    if (action === 'spotlight.next' || action === 'spotlight.prev') {
      const list = matches.filter(m => m.participant1_id || m.participant2_id)
      const idx = list.findIndex(m => m.id === state?.spotlight_match_id)
      const nx  = action === 'spotlight.next' ? Math.min(list.length - 1, idx + 1) : Math.max(0, idx - 1)
      const next = list[nx]
      if (next) return patch({ spotlight_match_id: next.id })
    }
  }

  const toggleMidi = async () => {
    if (midiOn) {
      if (detachRef.current) { detachRef.current(); detachRef.current = null }
      setMidiOn(false); return
    }
    try {
      setMidiErr('')
      const access = await tOpenMIDI()
      const inputs = tListInputs(access)
      const input = inputs.find(i => i.id === tLoadInputId()) || inputs[0]
      if (!input) { setMidiErr('Geen MIDI input gevonden'); return }
      const mappings = tLoadMappings()
      detachRef.current = tAttachInput(input, (evt) => {
        const key = tEventKey(evt); if (!key) return
        const spec = tNormalizeMapping(mappings[key])
        if (spec.action) runAction(spec.action)
      })
      setMidiOn(true)
    } catch (e) { setMidiErr(e.message) }
  }
  useEffect(() => () => { if (detachRef.current) detachRef.current() }, [])

  const VIEWS = [
    { key: 'lobby',     label: 'Lobby'     },
    { key: 'bracket',   label: 'Bracket'   },
    { key: 'spotlight', label: 'Spotlight' },
    { key: 'live',      label: 'Live'      },
    { key: 'winner',    label: 'Winnaar'   },
  ]

  if (!tournament?.features?.beamer) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Beamer view</CardTitle>
          <CardDescription>Zet "Beamer view" aan in Features om dit te gebruiken.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Beamer besturing</CardTitle>
          <CardDescription>Wat er op het grote scherm getoond wordt.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={midiOn ? 'default' : 'outline'} onClick={toggleMidi}>
            MIDI {midiOn ? 'aan' : 'uit'}
          </Button>
          <a href="/tournaments/midi" target="_blank" rel="noopener"
             className="inline-flex items-center gap-2 rounded-[4px] border border-border px-3 py-1.5 text-sm">
            MIDI config
          </a>
          <a href={`/tournaments/${tournament.id}/stage`} target="_blank" rel="noopener"
             className="inline-flex items-center gap-2 rounded-[4px] bg-acid px-3 py-1.5 text-primary-foreground text-sm font-medium">
            Open beamer &nearr;
          </a>
          <a href={`/tournaments/${tournament.id}/stage?overlay=1`} target="_blank" rel="noopener"
             className="inline-flex items-center gap-2 rounded-[4px] border border-border px-3 py-1.5 text-sm">
            OBS overlay
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {midiErr && <div className="text-xs text-signal">⚠ {midiErr}</div>}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">View</div>
          <div className="flex gap-2 flex-wrap">
            {VIEWS.map(v => (
              <Button key={v.key} size="sm"
                      variant={state?.current_view === v.key ? 'default' : 'outline'}
                      disabled={saving}
                      onClick={() => patch({ current_view: v.key })}>
                {v.label}
              </Button>
            ))}
          </div>
        </div>
        {(state?.current_view === 'spotlight' || state?.current_view === 'live') && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Spotlight match</div>
            <select
              className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
              value={state?.spotlight_match_id ?? ''}
              onChange={e => patch({ spotlight_match_id: e.target.value ? Number(e.target.value) : null })}
              disabled={saving}
            >
              <option value="">— kies match —</option>
              {matches.filter(m => m.participant1_id || m.participant2_id).map(m => (
                <option key={m.id} value={m.id}>
                  R{m.round}·M{m.match_number} — {m.participant1_name || 'tba'} vs {m.participant2_name || 'tba'}
                </option>
              ))}
            </select>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   Prize koppeling (track D)
================================================================== */
function PrizeCouplingCard({ tournament, participants, onSaved }) {
  const [cats, setCats] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!tournament?.features?.auto_prize) return
    prizesAPI.listCategories().then(r => setCats(r.data.categories || [])).catch(() => setCats([]))
  }, [tournament?.features?.auto_prize])

  if (!tournament?.features?.auto_prize) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Prijs uit prijzenkraam</CardTitle>
          <CardDescription>Zet "Automatisch prijs" aan in Features om dit te gebruiken.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const setPrize = async (prizeId) => {
    setBusy(true)
    try { await tournamentsAPI.setPrize(tournament.id, prizeId || null); await onSaved() }
    finally { setBusy(false) }
  }
  const award = async () => {
    setBusy(true)
    try {
      const r = await tournamentsAPI.awardPrize(tournament.id)
      await onSaved()
      alert(`Prijs "${r.data.prize_name}" uitgereikt aan winnaar.`)
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message))
    } finally { setBusy(false) }
  }

  const winner = participants.find(p => p.placement === 1)
  const flat = (cats || []).flatMap(c => (c.prizes || []).map(p => ({ ...p, catPrice: c.price, catName: c.name })))
  const currentPrize = flat.find(p => p.id === tournament.prize_id)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Prijs voor de winnaar</CardTitle>
        <CardDescription>Kies één item uit de prijzenkraam. Uitreiken kan als het toernooi klaar is en er een winnaar is.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {cats === null && <div className="text-sm text-muted-foreground">Prijzen laden…</div>}
        {cats !== null && (
          <select
            className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
            value={tournament.prize_id || ''}
            onChange={e => setPrize(e.target.value ? Number(e.target.value) : null)}
            disabled={busy}
          >
            <option value="">— geen prijs —</option>
            {(cats || []).map(cat => (
              <optgroup key={cat.id} label={`${cat.name} · ${cat.price} pt`}>
                {(cat.prizes || []).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        {currentPrize && (
          <div className="text-xs text-muted-foreground">
            Gekoppeld: <b className="text-acid">{currentPrize.name}</b> ({currentPrize.catName})
          </div>
        )}
        {tournament.prize_awarded_at && (
          <div className="text-xs text-live">✓ Uitgereikt op {new Date(tournament.prize_awarded_at).toLocaleString('nl-BE')}</div>
        )}
        {/* Award-knop weggehaald: uitreiken gebeurt in de Uitreiking-tab
            (bundelt punten + badge + prijs in één klik via /award-all).
            Hier alleen de configuratie: welke prijs krijgt de winnaar. */}
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   Points-per-placement uitreiking (track D)
================================================================== */
function PointsRewardCard({ tournament, participants, onSaved }) {
  const initial = tournament?.points_reward || {}
  const [first,   setFirst]   = useState(initial['1']       ?? 25)
  const [second,  setSecond]  = useState(initial['2']       ?? 15)
  const [third,   setThird]   = useState(initial['3']       ?? 10)
  const [dflt,    setDflt]    = useState(initial['default'] ?? 1)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const r = tournament?.points_reward || {}
    setFirst(r['1'] ?? 25); setSecond(r['2'] ?? 15); setThird(r['3'] ?? 10); setDflt(r['default'] ?? 1)
  }, [tournament?.id])

  if (!tournament?.features?.points_award) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Punten per plaats</CardTitle>
          <CardDescription>Zet "Punten per plaats" aan in Features om dit te gebruiken.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const save = async () => {
    setBusy(true)
    try {
      await tournamentsAPI.setPointsReward(tournament.id, {
        '1': Number(first) || 0, '2': Number(second) || 0, '3': Number(third) || 0,
        default: Number(dflt) || 0,
      })
      await onSaved()
    } finally { setBusy(false) }
  }
  const award = async () => {
    setBusy(true)
    try {
      const r = await tournamentsAPI.awardPoints(tournament.id)
      await onSaved()
      alert(`${r.data.awarded} punten-transacties aangemaakt.`)
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message))
    } finally { setBusy(false) }
  }

  const winner = participants.find(p => p.placement === 1)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Punten per plaats</CardTitle>
        <CardDescription>Automatische puntentoekenning naar deelnemers volgens hun plaatsing.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <PtsInput label="1e" value={first}  onChange={setFirst}  disabled={busy} />
          <PtsInput label="2e" value={second} onChange={setSecond} disabled={busy} />
          <PtsInput label="3e" value={third}  onChange={setThird}  disabled={busy} />
          <PtsInput label="Rest" value={dflt} onChange={setDflt}   disabled={busy} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy} onClick={save}>Opslaan</Button>
        </div>
        {tournament.points_awarded_at && (
          <div className="text-xs text-live">✓ Punten uitgereikt op {new Date(tournament.points_awarded_at).toLocaleString('nl-BE')}</div>
        )}
        <div className="text-xs text-muted-foreground pt-2 border-t border-border/40">
          Personal: punten naar de deelnemer. Team: elk teamlid krijgt de volle punten. No-shows krijgen niks.
          Uitreiken zelf gebeurt in de <strong>Uitreiking</strong>-tab.
        </div>
      </CardContent>
    </Card>
  )
}

function PtsInput({ label, value, onChange, disabled }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <input type="number" min={0}
             className="h-9 rounded-[4px] border border-border bg-background px-2 text-sm tabular-nums text-right"
             value={value} disabled={disabled}
             onChange={e => onChange(e.target.value)} />
    </label>
  )
}

/* ==================================================================
   Predictions tab (track D) — publiek gokt op match-winnaars
================================================================== */
function PredictionsTab({ tournament, matches, participants, currentUser, onRefresh }) {
  const [data, setData] = useState({ predictions: [], balance: 0 })
  const [busy, setBusy] = useState(false)

  const load = async () => {
    try {
      const r = await tournamentsAPI.listPredictions(tournament.id)
      setData({ predictions: r.data.predictions || [], balance: r.data.balance || 0 })
    } catch {}
  }
  useEffect(() => { load() }, [tournament.id])

  if (!currentUser) return null

  // Open matches waar user nog geen prediction op heeft en beide participants bekend zijn
  const myPreds = new Set(data.predictions.filter(p => p.user_id === currentUser.id).map(p => p.match_id))
  const openMatches = matches.filter(m =>
    m.status === 'pending'
    && m.participant1_id && m.participant2_id
    && !myPreds.has(m.id)
  )
  const myOpen = data.predictions.filter(p => p.user_id === currentUser.id && !p.resolved_at)
  const myResolved = data.predictions.filter(p => p.user_id === currentUser.id && p.resolved_at)
  const totalWon = myResolved.filter(p => p.correct).reduce((s, p) => s + p.points_wagered * 2, 0)
  const totalLost = myResolved.filter(p => !p.correct).reduce((s, p) => s + p.points_wagered, 0)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-baseline justify-between">
          <div>
            <CardTitle>Predicties</CardTitle>
            <CardDescription>
              Zet <strong>bar-punten</strong> in op wie een match gaat winnen.
              Voorspel je goed, krijg je <strong>2× je inzet</strong> terug — fout, dan zijn je punten weg.
              Kies een match hieronder, tik hoeveel je inzet, en kies een team.
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Jouw saldo</div>
            <div className="text-2xl font-bold text-acid tabular-nums">{data.balance}</div>
          </div>
        </CardHeader>
      </Card>

      {openMatches.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Open matches ({openMatches.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {openMatches.map(m => (
              <PredictWidget key={m.id} match={m} tournament={tournament} balance={data.balance}
                             onPlaced={async () => { await load(); await onRefresh?.() }}
                             disabled={busy} setBusy={setBusy} />
            ))}
          </CardContent>
        </Card>
      )}

      {myOpen.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Jouw open voorspellingen ({myOpen.length})</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border/40">
            {myOpen.map(p => (
              <div key={p.id} className="py-2 flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-medium">R{p.round}·M{p.match_number} — <span className="text-acid">{p.pick_team_name}</span></div>
                  <div className="text-xs text-muted-foreground">{p.match_status === 'in_progress' ? 'bezig' : 'wacht op start'}</div>
                </div>
                <div className="font-mono text-sm tabular-nums text-muted-foreground">-{p.points_wagered} pt ingezet</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {myResolved.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-baseline justify-between">
            <CardTitle className="text-base">Jouw historie ({myResolved.length})</CardTitle>
            <div className="text-xs font-mono">
              <span className="text-live">+{totalWon}</span> · <span className="text-signal">−{totalLost}</span>
            </div>
          </CardHeader>
          <CardContent className="divide-y divide-border/40">
            {myResolved.map(p => (
              <div key={p.id} className="py-2 flex items-center gap-3">
                <div className={`w-2 h-8 rounded ${p.correct ? 'bg-live' : 'bg-signal'}`} />
                <div className="flex-1">
                  <div className="font-medium">R{p.round}·M{p.match_number} — {p.pick_team_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.correct ? 'correct — 2× uitbetaling' : 'incorrect'}
                  </div>
                </div>
                <div className={`font-mono text-sm tabular-nums ${p.correct ? 'text-live' : 'text-signal'}`}>
                  {p.correct ? `+${p.points_wagered}` : `−${p.points_wagered}`}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {openMatches.length === 0 && myOpen.length === 0 && myResolved.length === 0 && (
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Geen open matches om op te gokken. Wacht op nieuwe pairings.
        </CardContent></Card>
      )}
    </div>
  )
}

function PredictWidget({ match, tournament, balance, onPlaced, disabled, setBusy }) {
  const [pick, setPick] = useState(null)
  const [wager, setWager] = useState(10)
  const place = async () => {
    if (!pick || wager < 1) return
    setBusy(true)
    try {
      await tournamentsAPI.placePrediction(tournament.id, match.id, pick, wager)
      await onPlaced()
      setPick(null)
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message))
    } finally { setBusy(false) }
  }
  return (
    <div className="rounded border border-border p-3 space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Round {match.round} · Match {match.match_number}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant={pick === match.participant1_id ? 'default' : 'outline'} disabled={disabled}
                onClick={() => setPick(match.participant1_id)}>
          {match.participant1_name}
        </Button>
        <Button variant={pick === match.participant2_id ? 'default' : 'outline'} disabled={disabled}
                onClick={() => setPick(match.participant2_id)}>
          {match.participant2_name}
        </Button>
      </div>
      {pick && (
        <div className="flex items-center gap-2 pt-1">
          <label className="text-xs text-muted-foreground">Inzet</label>
          <input type="number" min={1} max={balance}
                 className="w-24 h-9 rounded-[4px] border border-border bg-background px-2 text-sm tabular-nums text-right"
                 value={wager} onChange={e => setWager(Number(e.target.value) || 0)} />
          <span className="text-xs text-muted-foreground">pt</span>
          <div className="flex-1" />
          <Button disabled={disabled || wager < 1 || wager > balance} onClick={place}>
            Predict {wager} pt
          </Button>
        </div>
      )}
    </div>
  )
}

/* ==================================================================
   Media tab (track D · screenshots-timeline)
================================================================== */
function MediaTab({ tournamentId }) {
  const [items, setItems] = useState(null)
  useEffect(() => {
    if (!tournamentId) return
    screenshotsAPI.getScreenshots({ tournament_id: tournamentId, limit: 200 })
      .then(r => setItems(r.data.screenshots || r.data || []))
      .catch(() => setItems([]))
  }, [tournamentId])

  if (items === null) return <div className="text-sm text-muted-foreground">Laden…</div>
  if (items.length === 0) return (
    <Card><CardContent className="p-8 text-center text-muted-foreground">
      Nog geen screenshots voor dit toernooi. Upload er één via <a href="/screenshots" className="text-acid underline">Screenshots</a> en link het aan dit toernooi.
    </CardContent></Card>
  )

  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {items.map(it => (
        <a key={it.id} href={`/screenshots?open=${it.id}`}
           className="rounded-md border border-border bg-card/60 hover:border-acid overflow-hidden group no-underline">
          <div className="aspect-video bg-background overflow-hidden">
            {it.file_path && (
              <img src={screenshotsAPI.getScreenshotUrl(it.file_path)} alt={it.title || ''}
                   loading="lazy"
                   className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
            )}
          </div>
          <div className="p-2">
            <div className="text-xs font-medium truncate">{it.title}</div>
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
              <span>♥ {it.likes_count ?? 0}</span>
              <span>💬 {it.comments_count ?? 0}</span>
              {it.username && <span className="ml-auto truncate">door {it.username}</span>}
            </div>
          </div>
        </a>
      ))}
    </div>
  )
}

/* ==================================================================
   Badge koppeling (track D · winnaar krijgt badge)
================================================================== */
function BadgeCouplingCard({ tournament, participants, onSaved }) {
  const [types, setTypes] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!tournament?.features?.badges) return
    badgeAPI.getAllTypes().then(r => setTypes(Array.isArray(r.data?.badges) ? r.data.badges : Array.isArray(r.data) ? r.data : [])).catch(() => setTypes([]))
  }, [tournament?.features?.badges])

  if (!tournament?.features?.badges) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Winnaar-badge</CardTitle>
          <CardDescription>Zet "Badges" aan in Features om dit te gebruiken.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const setBadge = async (badgeId) => {
    setBusy(true)
    try { await tournamentsAPI.setBadge(tournament.id, badgeId || null); await onSaved() }
    finally { setBusy(false) }
  }
  const award = async () => {
    setBusy(true)
    try {
      const r = await tournamentsAPI.awardBadge(tournament.id)
      await onSaved()
      alert(`Badge uitgereikt aan ${r.data.awarded} user(s).`)
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message))
    } finally { setBusy(false) }
  }

  const winner = participants.find(p => p.placement === 1)
  const currentBadge = (types || []).find(b => b.id === tournament.badge_type_id)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Winnaar-badge</CardTitle>
        <CardDescription>Blijvende badge naast de winnaar's naam. Beheer badges in de badges-module.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {types === null && <div className="text-sm text-muted-foreground">Badges laden…</div>}
        {types !== null && types.length === 0 && (
          <div className="text-sm text-muted-foreground">
            Nog geen badge-types. Maak er eerst één aan via <a href="/admin?tab=badges" className="text-acid underline">Admin → Badges</a>.
          </div>
        )}
        {types !== null && types.length > 0 && (
          <select
            className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
            value={tournament.badge_type_id || ''}
            onChange={e => setBadge(e.target.value ? Number(e.target.value) : null)}
            disabled={busy}
          >
            <option value="">— geen badge —</option>
            {types.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        {currentBadge && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            Gekoppeld:
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border" style={{ borderColor: currentBadge.color, color: currentBadge.color }}>
              {currentBadge.name}
            </span>
          </div>
        )}
        {tournament.badges_awarded_at && (
          <div className="text-xs text-live">✓ Uitgereikt op {new Date(tournament.badges_awarded_at).toLocaleString('nl-BE')}</div>
        )}
        <div className="pt-2 border-t border-border/40 text-xs text-muted-foreground">
          Uitreiken zelf gebeurt in de <strong>Uitreiking</strong>-tab (bundelt punten + badge + prijs).
        </div>
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   Ready-check per match — track B
================================================================== */
function MyMatchReadyCard({ tournament, matches, participants, currentUser, onSaved }) {
  const [busy, setBusy] = useState(null)
  if (!tournament?.features?.ready_check || !currentUser) return null

  const me = participants.find(p =>
    p.user_id === currentUser.id ||
    (p.team_members || []).some(tm => tm.user_id === currentUser.id)
  )
  if (!me) return null

  const myMatches = matches.filter(m =>
    m.status === 'pending' &&
    (m.participant1_id === me.id || m.participant2_id === me.id)
  )
  if (myMatches.length === 0) return null

  const doReady = async (matchId) => {
    setBusy(matchId)
    try { await tournamentsAPI.markMatchReady(tournament.id, matchId); await onSaved() }
    finally { setBusy(null) }
  }

  return (
    <Card className="mb-4" style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
      <CardHeader>
        <CardTitle className="text-base">Jouw komende matches</CardTitle>
        <CardDescription>Klik "Ready" zodra je aan je scherm zit. De organisatie ziet wanneer beide klaar zijn.</CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border/40">
        {myMatches.map(m => {
          const readyIds = m.ready_participant_ids || []
          const iAmReady = readyIds.includes(me.id)
          const opponentId = m.participant1_id === me.id ? m.participant2_id : m.participant1_id
          const opponentName = m.participant1_id === me.id ? m.participant2_name : m.participant1_name
          const opponentReady = readyIds.includes(opponentId)
          return (
            <div key={m.id} className="py-2 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-medium">R{m.round}·M{m.match_number} — vs {opponentName || 'tba'}</div>
                <div className="text-xs text-muted-foreground">
                  {iAmReady ? <span className="text-live">✓ jij ready</span> : <span>jij niet ready</span>}
                  {' · '}
                  {opponentReady ? <span className="text-live">tegenstander ready</span> : <span className="text-muted-foreground">tegenstander wacht</span>}
                </div>
              </div>
              {!iAmReady && (
                <Button size="sm" onClick={() => doReady(m.id)} disabled={busy === m.id}>Ready</Button>
              )}
              {iAmReady && <span className="font-mono text-xs text-live">✓ ready</span>}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

/* Verplichte Uitreiking-tab — één plek voor alles: punten, badge, prijs.
   De losse Award-knoppen in Instellingen-tab blijven bestaan voor gedeeltelijke
   re-runs; dit paneel bundelt de "één klik" via /award-all. */
function UitreikingPanel({ tournament, onDone }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  if (!tournament) return null

  const isCompleted = tournament.status === 'completed'
  const doneP = !!tournament.points_awarded_at
  const doneB = !!tournament.badges_awarded_at
  const doneG = !!tournament.prize_awarded_at
  // "heeftPunten": alleen tellen als er echt een non-lege points_reward-config
  // is. Zonder deze check bleef `allesKlaar` false wanneer de admin geen
  // punten had geconfigureerd — de uitreik-flow kwam dan nooit uit "wacht".
  const heeftPunten = !!(tournament.points_reward &&
    (typeof tournament.points_reward === 'object'
      ? Object.keys(tournament.points_reward).length > 0
      : String(tournament.points_reward).length > 0))
  const heeftBadge = !!tournament.badge_type_id
  const heeftPrijs = !!tournament.prize_id
  const nietsGeconfigd = !heeftPunten && !heeftBadge && !heeftPrijs
  const allesKlaar = !nietsGeconfigd
    && (!heeftPunten || doneP)
    && (!heeftBadge  || doneB)
    && (!heeftPrijs  || doneG)

  const doAll = async () => {
    setBusy(true); setMsg('')
    try {
      const r = await tournamentsAPI.awardAll(tournament.id)
      const s = r.data?.summary || {}
      const parts = []
      if (s.points) parts.push('punten')
      if (s.badges) parts.push('badge')
      if (s.prize)  parts.push('prijs')
      const errs = (s.errors || []).map(e => `${e.step}: ${e.message}`).join(' · ')
      setMsg(parts.length
        ? `Uitgereikt: ${parts.join(', ')}${errs ? ` — fouten: ${errs}` : ''}`
        : (errs || 'Er viel niets meer uit te reiken.'))
      onDone?.()
    } catch (e) { setMsg(`Fout: ${e.response?.data?.error ?? e.message}`) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      {!isCompleted && (
        <Card style={{ borderLeft: '3px solid rgb(var(--peach-rgb))' }}>
          <CardContent className="p-5 text-sm text-muted-foreground">
            Toernooi staat nog op <strong>{tournament.status}</strong>. Zet eerst op "completed" (via Instellingen → status) voor je kunt uitreiken.
          </CardContent>
        </Card>
      )}

      <Card style={{ borderLeft: `3px solid ${allesKlaar ? 'rgb(var(--live-rgb))' : (nietsGeconfigd ? 'hsl(var(--muted-foreground))' : 'rgb(var(--peach-rgb))')}` }}>
        <CardContent className="p-5 space-y-4">
          <div className="font-semibold text-lg">
            {nietsGeconfigd
              ? <span className="text-muted-foreground">Geen uitreiking geconfigureerd</span>
              : allesKlaar
                ? <span className="text-emerald-400">✓ Alles uitgereikt</span>
                : <span className="text-peach">Uitreiking klaar te zetten</span>}
          </div>

          <div className="grid gap-2 text-sm">
            <StatusRij label="Punten"
                       done={doneP}
                       done_at={tournament.points_awarded_at}
                       leeg={heeftPunten ? null : "Geen punten-config (Instellingen → Punten per plaats)"} />
            <StatusRij label="Badge"
                       done={doneB}
                       done_at={tournament.badges_awarded_at}
                       leeg={heeftBadge ? null : "Geen winnaar-badge gekoppeld (Instellingen → Badge)"} />
            <StatusRij label="Prijs"
                       done={doneG}
                       done_at={tournament.prize_awarded_at}
                       leeg={heeftPrijs ? null : "Geen prijs gekoppeld (Instellingen → Prijs)"} />
          </div>

          {nietsGeconfigd ? (
            <p className="text-sm text-muted-foreground">
              Dit toernooi heeft geen punten, badge of prijs gekoppeld — er valt niks uit te reiken.
              Configureer één (of meer) in de <strong>Instellingen</strong>-tab als je toch iets wil toekennen.
            </p>
          ) : (
            <Button size="lg" onClick={doAll} disabled={busy || !isCompleted || allesKlaar}>
              <Award className="h-4 w-4 mr-2" />
              {busy ? 'Bezig…' : allesKlaar ? 'Alles al uitgereikt' : 'Alles uitreiken'}
            </Button>
          )}
          {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
          <p className="text-xs text-muted-foreground">
            Individueel uitreiken (of opnieuw uitreiken) kan in de <strong>Instellingen</strong>-tab.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function StatusRij({ label, done, done_at, leeg }) {
  if (leeg) return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <span className="w-16 shrink-0">{label}</span>
      <span className="text-xs italic">{leeg}</span>
    </div>
  )
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 shrink-0">{label}</span>
      {done
        ? <span className="text-emerald-400 text-xs">✓ {new Date(done_at).toLocaleString('nl-BE')}</span>
        : <span className="text-peach text-xs">Nog niet uitgereikt</span>}
    </div>
  )
}
/* Kebab-menu voor destructieve toernooi-acties. Restart/Cancel/Delete zaten
   voordien als losse knoppen naast Ververs — één klik weg. Nu achter een
   MoreVertical zodat je 'm niet per ongeluk raakt. */
function MeerActies({ onRestart, onCancel, onDelete, restarting, canceling, deleting }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="icon" onClick={() => setOpen(o => !o)}
              title="Meer acties">
        <MoreVertical className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-56 rounded-md border border-border bg-card shadow-lg py-1">
          {onRestart && (
            <button className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-peach flex items-center gap-2"
                    disabled={restarting}
                    onClick={() => { setOpen(false); onRestart() }}>
              {restarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Herstart toernooi
            </button>
          )}
          {onCancel && (
            <button className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-peach flex items-center gap-2"
                    disabled={canceling}
                    onClick={() => { setOpen(false); onCancel() }}>
              {canceling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Annuleer toernooi
            </button>
          )}
          {onDelete && (
            <button className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-signal flex items-center gap-2"
                    disabled={deleting}
                    onClick={() => { setOpen(false); onDelete() }}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Verwijder toernooi
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/* Format-picker die verschijnt als tournament.type === 'pending'. Kiest het
   toernooi-format nu de deelnemer-count vaststaat. Elk format heeft een
   minimum aantal spelers — de knoppen die niet halen zijn disabled met de
   reden erbij. Metadata leeft in lib/tournamentFormats — enkelvoudig punt. */
const FORMAT_CHOICES = TOURNAMENT_FORMATS
function TournamentFormatPicker({ tournament, participants, onChosen }) {
  const [choice, setChoice] = useState('single_elimination')
  const [rounds, setRounds] = useState(tournament?.rounds || 1)
  const [busy, setBusy]   = useState(false)
  const [err, setErr]     = useState(null)
  const activeCount = participants.filter(p => !p.no_show).length

  async function save() {
    setBusy(true); setErr(null)
    try {
      await tournamentsAPI.updateTournamentType(tournament.id, { type: choice, rounds: Number(rounds) || 1 })
      onChosen?.()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-md border-2 border-acid/60 bg-acid/5 p-4 mb-4">
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <div>
          <div className="eyebrow text-acid">Kies het format</div>
          <div className="text-sm text-muted-foreground">
            Er {activeCount === 1 ? 'is 1 speler' : `zijn ${activeCount} spelers`} ingeschreven —
            kies het toernooi-format dat daarbij past.
          </div>
        </div>
        <span className="font-mono text-xs uppercase tracking-widest px-2 py-1 rounded bg-signal/10 text-signal border border-signal/30">
          Format nog te kiezen
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
        {FORMAT_CHOICES.map(f => {
          const tooFew = activeCount < f.min
          const active = choice === f.key
          return (
            <button key={f.key}
                    type="button"
                    disabled={tooFew}
                    onClick={() => setChoice(f.key)}
                    className={`text-left px-3 py-2 rounded border transition-colors ${
                      active
                        ? 'border-acid bg-acid/10'
                        : tooFew
                          ? 'border-border/30 opacity-40 cursor-not-allowed'
                          : 'border-border hover:border-acid/60'
                    }`}>
              <div className="text-sm font-medium">{f.label}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{f.hint}</div>
              {tooFew && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-signal mt-1">
                  Min. {f.min} spelers
                </div>
              )}
            </button>
          )
        })}
      </div>

      {(choice === 'deathmatch' || choice === 'ffa') && (
        <div className="mt-3 flex items-center gap-2">
          <label htmlFor="dm-rounds" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Aantal rondes
          </label>
          <input id="dm-rounds" type="number" min="1" max="20"
                 value={rounds} onChange={e => setRounds(e.target.value)}
                 className="w-20 h-8 rounded border border-border bg-transparent px-2 text-sm" />
          {choice === 'ffa' && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              — deelnemers worden per ronde herverdeeld
            </span>
          )}
        </div>
      )}

      {err && <div className="text-signal text-xs mt-3">{err}</div>}

      <div className="flex justify-end mt-3">
        <Button onClick={save} disabled={busy || activeCount < 2}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Format vastleggen
        </Button>
      </div>
    </div>
  )
}

/* Generieke klassement-view voor niet-score-based formats.
   Sorteert deelnemers op placement (NULLS LAST) en toont voor elk:
   plaats-badge (🥇🥈🥉 of #N of tekst-label zoals "Halvefinalist"/"Top 8"),
   naam, aantal match-wins in dit toernooi. Voor swiss/round_robin ook de
   score-metriek relevant; anders enkel de plek. */
function GenericStandings({ tournament, participants, matches }) {
  const winsPer = useMemo(() => {
    const m = new Map()
    for (const p of participants || []) m.set(p.id, 0)
    for (const mt of matches || []) {
      if (mt.status !== 'completed' || !mt.winner_id) continue
      if (m.has(mt.winner_id)) m.set(mt.winner_id, m.get(mt.winner_id) + 1)
    }
    return m
  }, [participants, matches])

  const gesorteerd = useMemo(() => {
    return (participants || []).slice().sort((a, b) => {
      const pa = a.placement == null ? Infinity : a.placement
      const pb = b.placement == null ? Infinity : b.placement
      if (pa !== pb) return pa - pb
      return String(a.team_name || '').localeCompare(String(b.team_name || ''))
    })
  }, [participants])

  if (gesorteerd.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Nog geen deelnemers ingeschreven.</p>
      </div>
    )
  }

  const heeftPlacements = gesorteerd.some(p => p.placement != null)
  if (!heeftPlacements) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">
          Nog geen klassement — verschijnt zodra er match-resultaten binnenkomen.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {gesorteerd.map(p => {
        const wins = winsPer.get(p.id) || 0
        const plek = p.placement
        const label = p.placement_label
        const cardTone = plek === 1
          ? 'bg-yellow-500/15 border-yellow-500/40'
          : plek === 2
          ? 'bg-muted/40 border-muted-foreground/40'
          : plek === 3
          ? 'bg-orange-500/15 border-orange-500/40'
          : 'bg-card/60 border-border'
        return (
          <div key={p.id}
               className={`flex items-center gap-4 p-3 rounded-lg border ${cardTone} ${p.no_show ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-card border border-border shrink-0">
              {plek === 1 ? <span className="text-2xl">🥇</span>
              : plek === 2 ? <span className="text-2xl">🥈</span>
              : plek === 3 ? <span className="text-2xl">🥉</span>
              : plek != null
                ? <span className="text-sm font-bold text-muted-foreground">#{plek}</span>
                : <span className="text-xs text-muted-foreground">—</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold truncate">{p.team_name}</div>
              {p.username && (
                <div className="text-xs text-muted-foreground">@{p.username}</div>
              )}
              {label && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                  {label}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-acid tabular-nums">{wins}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {wins === 1 ? 'win' : 'wins'}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
